import { getHFieldWithAppliedHeightScale } from "./terrainFloor";

function clampComponent(v, min, max) {
  return Math.min(max, Math.max(min, v));
}

/**
 * Height of the standard floor heightfield at model XY (same grid as scene floor / library drop).
 * @param {import("../types/scene").HField | null | undefined} hfield
 * @param {number} x
 * @param {number} y
 * @returns {number | null}
 */
export function sampleHFieldHeightAtModelXY(hfield, x, y) {
  const effective = getHFieldWithAppliedHeightScale(hfield);
  if (!effective || !Array.isArray(effective.size) || !Array.isArray(effective.data)) return null;
  const nrow = Math.max(2, Math.floor(Number(effective.nrow) || 2));
  const ncol = Math.max(2, Math.floor(Number(effective.ncol) || 2));
  if (effective.data.length < nrow * ncol) return null;

  const width = 2 * (Number(effective.size[0]) || 1);
  const height = 2 * (Number(effective.size[1]) || width / 2 || 1);
  const elevation = Number(effective.size[2]) || 0;
  if (width <= 0 || height <= 0) return null;

  const maxCol = ncol - 1;
  const maxRow = nrow - 1;
  let colF = ((x + width / 2) / width) * maxCol;
  let rowF = ((height / 2 - y) / height) * maxRow;
  if (colF < 0 || colF > maxCol || rowF < 0 || rowF > maxRow) return null;
  colF = clampComponent(colF, 0, maxCol);
  rowF = clampComponent(rowF, 0, maxRow);

  const c0 = Math.floor(colF);
  const r0 = Math.floor(rowF);
  const c1 = Math.min(maxCol, c0 + 1);
  const r1 = Math.min(maxRow, r0 + 1);
  const dc = colF - c0;
  const dr = rowF - r0;
  const heightAt = (row, col) => (Number(effective.data[row * ncol + col]) || 0) * elevation;
  const z00 = heightAt(r0, c0);
  const z10 = heightAt(r0, c1);
  const z01 = heightAt(r1, c0);
  const z11 = heightAt(r1, c1);
  const z0 = z00 * (1 - dc) + z10 * dc;
  const z1 = z01 * (1 - dc) + z11 * dc;
  return z0 * (1 - dr) + z1 * dr;
}

/**
 * @param {import("../types/scene").HField | null | undefined} hfield
 * @param {{ min: { x: number; y: number; z?: number }; max: { x: number; y: number; z?: number } }} bounds Footprint in floor model space (e.g. THREE.Box3 corners).
 * @param {number} [divisions]
 * @returns {number | null}
 */
export function sampleMaxHFieldHeightInModelBounds(hfield, bounds, divisions = 4) {
  if (!bounds) return null;
  let maxHeight = null;
  const steps = Math.max(1, Math.floor(divisions));
  for (let ix = 0; ix <= steps; ix += 1) {
    const tx = ix / steps;
    const x = bounds.min.x + (bounds.max.x - bounds.min.x) * tx;
    for (let iy = 0; iy <= steps; iy += 1) {
      const ty = iy / steps;
      const y = bounds.min.y + (bounds.max.y - bounds.min.y) * ty;
      const h = sampleHFieldHeightAtModelXY(hfield, x, y);
      if (h == null || !Number.isFinite(h)) continue;
      maxHeight = maxHeight == null ? h : Math.max(maxHeight, h);
    }
  }
  return maxHeight;
}

export function getHFieldHeightRange(hfield) {
  const effective = getHFieldWithAppliedHeightScale(hfield);
  if (!effective || !Array.isArray(effective.data) || !Array.isArray(effective.size)) return null;
  const elevation = Number(effective.size[2]) || 0;
  let min = Infinity;
  let max = -Infinity;
  effective.data.forEach((value) => {
    const h = (Number(value) || 0) * elevation;
    if (h < min) min = h;
    if (h > max) max = h;
  });
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  return { min, max };
}
